import React, { ReactElement, useState } from 'react'
import useAnalyticsContext from 'src/analytics/useAnalyticsContext'
import GoogleWorkspaceIntegrationSetupModal from 'src/component-modal/GoogleWorkspaceIntegrationSetupModal/GoogleWorkspaceIntegrationSetupModal'
import { useTranslate } from 'src/localization/Trns'
import { QueryError } from 'src/queries/network.utils'
import { GoogleCalendarIntegrationResponse } from 'src/queries/organizations/calendarIntegrations/calendarIntegrations.models'
import { useConnectGoogleCalendarIntegration } from 'src/queries/organizations/calendarIntegrations/calendarIntegrations.queries'

type GoogleCalendarIntegrationModalProps = {
  isOpen: boolean
  onClose: () => void
  organizationId: string
  integration: GoogleCalendarIntegrationResponse
}

const errorCategory = (error: QueryError): string => {
  switch (error.errorCode) {
    case 'CALENDAR_DELEGATION_NOT_READY':
      return 'delegation_not_ready'
    case 'CALENDAR_SUBJECT_NOT_WORKSPACE_USER':
      return 'subject_not_workspace_user'
    case 'CALENDAR_INVALID_ADMIN_EMAIL':
      return 'invalid_admin_email'
    default:
      return 'provider_unavailable'
  }
}

export default function GoogleCalendarIntegrationModal({
  isOpen,
  onClose,
  organizationId,
  integration,
}: GoogleCalendarIntegrationModalProps): ReactElement {
  const { analytics } = useAnalyticsContext()
  const translate = useTranslate()
  const [verifyError, setVerifyError] = useState<QueryError | null>(null)
  const [connected, setConnected] = useState(false)
  const connect = useConnectGoogleCalendarIntegration({
    onSuccess: () => {
      setVerifyError(null)
      setConnected(true)
      analytics.track('calendar integration', {
        action: 'connect_succeeded',
        provider: 'GOOGLE',
      })
    },
    onError: (error) => {
      setVerifyError(error)
      analytics.track('calendar integration', {
        action: 'connect_failed',
        provider: 'GOOGLE',
        errorCategory: errorCategory(error),
      })
    },
  })

  const handleClose = () => {
    setVerifyError(null)
    setConnected(false)
    onClose()
  }

  const verificationMessage = () => {
    switch (verifyError?.errorCode) {
      case 'CALENDAR_DELEGATION_NOT_READY':
        return translate('Google Workspace delegation is not ready yet.')
      case 'CALENDAR_SUBJECT_NOT_WORKSPACE_USER':
        return translate(
          'Your work email is not a user in this Google Workspace domain.',
        )
      case 'CALENDAR_INVALID_ADMIN_EMAIL':
        return translate(
          'Your verified work email is required to verify this connection.',
        )
      default:
        return translate(
          'Google Calendar could not be verified. Try again later.',
        )
    }
  }

  return (
    <GoogleWorkspaceIntegrationSetupModal
      isOpen={isOpen}
      onClose={handleClose}
      integrationName={translate('Google Calendar')}
      setup={integration.setup}
      prerequisiteDescription={translate(
        'A Google Workspace super admin must authorize company-wide read-only access. Employees do not connect their calendars individually.',
      )}
      prerequisiteItems={[
        translate('Use your verified company email to verify setup.'),
        translate(
          'Only eligible events from employee primary calendars are used as temporary expense context.',
        ),
        translate(
          'Private and confidential events are excluded, and calendar content is not stored.',
        ),
      ]}
      verificationDescription={translate(
        'After the Google Admin grant is saved, verify it using your signed-in work email.',
      )}
      successDescription={translate(
        'Employees in the connected Workspace domain are now covered without individual sign-in.',
      )}
      verifyErrorMessage={verifyError ? verificationMessage() : undefined}
      verifyPending={connect.isPending}
      connected={connected}
      onVerify={() => connect.mutate({ orgId: organizationId })}
      onCopy={() =>
        analytics.track('calendar integration', {
          action: 'instructions_copied',
          provider: 'GOOGLE',
        })
      }
    />
  )
}
